import React, { ReactElement, useEffect, useState } from 'react';

import clsx from 'clsx';

import { initialVerificationCodeValues } from 'actions/account';
import FormBody from 'components/FormBody';
import FormHeader from 'components/FormHeader';
import { CheckIcon, ErrorCrossIcon, ReloadIcon } from 'components/Icons';
import Loader from 'components/Loader';
import StatusMessage from 'components/StatusMessage';
import VerificationCodeInput from 'components/VerificationCodeInput';
import { RESEND_CODE_TIMEOUT } from 'constants/constants';
import useTabletCheck from 'hooks/useTabletCheck';
import useTimer from 'hooks/useTimer';

import styles from './CodeVerificationForm.module.scss';

export interface VerificationFormProps {
  handleVerificationCodeComplete: (code: string) => Promise<void>;
  isCodeVerifying: boolean;
  isCodeValid: boolean | null;
  title?: string;
  subtitle?: string;
  formBodyClassName?: string;
  onResendCode?: () => void;
  alternativeFormHeader?: ReactElement;
  onTimerChange?: (isRunning: boolean) => void;
}

const CodeVerificationForm = ({
  handleVerificationCodeComplete,
  isCodeVerifying,
  isCodeValid,
  title,
  subtitle,
  onResendCode,
  alternativeFormHeader,
  onTimerChange,
  formBodyClassName,
}: VerificationFormProps) => {
  const [verificationCodeValues, setVerificationCodeValues] = useState<string[]>(initialVerificationCodeValues);
  const { restart, seconds, isRunning } = useTimer(RESEND_CODE_TIMEOUT);
  const isStatusMessageVisible = isCodeValid !== null && !isCodeVerifying;
  const isFormDisabled =
    verificationCodeValues.join('').length === verificationCodeValues.length ||
    isCodeValid === false ||
    isCodeVerifying;
  const isTablet = useTabletCheck();

  useEffect(() => {
    if (onTimerChange) {
      onTimerChange(isRunning);
    }
  }, [isRunning, onTimerChange]);

  const renderStatus = () => {
    if (isCodeValid) {
      return (
        <StatusMessage
          message="Correct Code"
          endAdornment={() => <CheckIcon className={styles.checkIcon} />}
          className={styles.successMessage}
        />
      );
    }

    return (
      <StatusMessage
        message="Invalid Code"
        endAdornment={() => <ErrorCrossIcon className={styles.crossIcon} />}
        className={styles.errorMessage}
      />
    );
  };

  const handleVerificationCodeValuesChange = async (values: string[]) => {
    setVerificationCodeValues(values);

    const code = values.join('');

    if (code.length === values.length) {
      await handleVerificationCodeComplete(code);
    }
  };

  const handleOnResend = () => {
    setVerificationCodeValues(initialVerificationCodeValues);

    if (onResendCode) {
      restart();
      onResendCode();
    }
  };

  const renderResendCode = () => {
    if (isRunning) {
      return (
        <div className={styles.timerMessage}>
          You can request another code in <span className={styles.secondsText}>{seconds}s</span>
        </div>
      );
    }

    if (isCodeValid === false || !isRunning) {
      return (
        <button
          disabled={!!isCodeValid}
          type="button"
          className={clsx(styles.reloadButton, !!isCodeValid && styles.reloadButtonDisabled)}
          onClick={handleOnResend}
        >
          <StatusMessage
            message="Click here to resend code"
            startAdornment={() => <ReloadIcon />}
            startAdornmentClassname={clsx(styles.reloadIcon, !!isCodeValid && styles.reloadIconDisabled)}
            className={clsx(styles.resendMessage, !!isCodeValid && styles.resendMessageDisabled)}
          />
        </button>
      );
    }
  };

  const renderBodyForm = () => {
    return (
      <>
        <div className={styles.codesContainer}>
          <VerificationCodeInput
            verificationCodeValues={verificationCodeValues}
            onVerificationCodeValuesChange={handleVerificationCodeValuesChange}
            isNumberInput
            disabled={isFormDisabled}
          />
          {isCodeVerifying && <Loader size={20} customContainerClassname={styles.loaderContainer} />}
          {isStatusMessageVisible && <div className={styles.statusContainer}>{renderStatus()}</div>}
        </div>
        {renderResendCode()}
      </>
    );
  };

  return (
    <>
      {alternativeFormHeader || <FormHeader title={title} subtitle={subtitle} percent={!isTablet ? 30 : undefined} />}
      <FormBody formBodyClassName={clsx(styles.form, formBodyClassName)}>{renderBodyForm()}</FormBody>
    </>
  );
};

export default CodeVerificationForm;
